import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getResult } from '../api/client';
import { DownloadPdfButton } from '../components/results/DownloadPdfButton';
import { ScoreBreakdown } from '../components/results/ScoreBreakdown';
import { ShareButton } from '../components/results/ShareButton';
import { StyleCard } from '../components/results/StyleCard';
import { GetResultResponse } from '../types';

export function ResultPage() {
  const { id } = useParams<{ id: string }>();
  const [result, setResult] = useState<GetResultResponse | null>(null);
  const [loading, setLoading] = useState(!!id);
  const [error, setError] = useState<string | null>(id ? null : 'Invalid result link.');

  useEffect(() => {
    if (!id) return;

    getResult(id)
      .then((data) => {
        setResult(data);
        setError(null);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Could not load result.');
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="w-10 h-10 border-4 border-cream/40 border-t-scarlet rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !result) {
    return (
      <div className="text-center py-16">
        <p className="text-brand-slate mb-4">{error ?? 'Result not found.'}</p>
        <Link to="/" className="text-sm text-scarlet underline hover:text-scarlet-dark">
          Take the quiz
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-brand">
          {result.name}'s Social Style Results
        </h1>
        <p className="text-brand-slate mt-1 text-sm">
          Completed on{' '}
          {new Date(result.submittedAt).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </p>
      </div>

      {/* Score breakdown */}
      <ScoreBreakdown scores={result.scores} />

      {/* Primary style */}
      <StyleCard styleKey={result.dominantStyle} isPrimary scores={result.scores} />

      {/* Backup style */}
      <StyleCard styleKey={result.backupStyle} isPrimary={false} scores={result.scores} />

      {/* Actions */}
      <div className="flex flex-wrap gap-3 pt-2">
        <DownloadPdfButton result={result} />
        <ShareButton />
        <Link
          to="/"
          className="
            inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-brand/20
            text-brand font-medium text-sm
            hover:border-brand/40 hover:bg-porcelain
            focus:outline-none focus:ring-2 focus:ring-scarlet/40 focus:ring-offset-2
            transition-all duration-150
          "
        >
          Take the quiz again
        </Link>
      </div>
    </div>
  );
}
